import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { RelatedKnowledgeAuto } from "@/components/site/RelatedKnowledgeAuto";
import { breadcrumbs, faqPage, qaPage, speakable, aeoGeo } from "@/lib/seo/schema";

const ANSWER =
  "Ett manus till film kostar i Sverige 2026 typiskt 4 000–75 000 kr. Kortformat för sociala medier: 4 000–9 000 kr. Manus till företagsfilm: 8 000–20 000 kr. Reklamfilmsmanus med koncept och kanalversioner: 25 000–75 000 kr. Kampanjkoncept som ska bära flera filmer: från 60 000 kr. Priset drivs av om koncept ingår, hur många idéspår som presenteras, antalet versioner och hur många revideringsrundor som behövs.";

const FAQ: LandingFAQ[] = [
  {
    q: "Varför skiljer priset så mycket?",
    a: "Den största enskilda faktorn är om konceptarbetet ingår. Att skriva ut ett färdigt koncept är hantverk med förutsägbar tidsåtgång; att hitta idén är research, gallring och presentation av flera spår.",
  },
  {
    q: "Betalar man per timme eller per uppdrag?",
    a: "Vi arbetar med fast uppdragspris. Timdebitering straffar kunden vid revideringar och gör budgeten omöjlig att förutse. Två revideringsrundor ingår alltid i priset.",
  },
  {
    q: "Ingår manus om ni gör hela filmen?",
    a: "Ja. Vid Prime-produktion ingår manus och koncept i projektpriset, och i Pulse-abonnemang från 14 995 kr/mån ingår löpande manus och innehållsplan.",
  },
  {
    q: "Vad kostar det att bara få manuset granskat?",
    a: "En manusgranskning med skriftlig återkoppling och produktionsteknisk kommentar ligger på 4 000–8 000 kr. Det är ofta värt pengarna när ni skrivit själva men vill veta om filmen går att spela in inom budget.",
  },
  {
    q: "Kostar storyboard extra?",
    a: "Ja, det är ett eget steg: 6 000–15 000 kr för en skiss-storyboard och 18 000–35 000 kr för en detaljerad board till reklamfilm.",
  },
];

const TITLE = "Vad kostar ett manus till film? Priser 2026 | Fenny";
const DESCRIPTION =
  "Vad kostar ett manus till film eller reklamfilm i Sverige? Prisspann 4 000–75 000 kr beroende på format och om koncept ingår. Kostnadsdrivare och exempel.";

export const Route = createFileRoute("/vad-kostar-ett-manus")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Vad kostar ett manus till film? — Fenny" },
      { property: "og:description", content: "Konkreta prisspann för manus, koncept och storyboard i Sverige 2026." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://fenny.se/vad-kostar-ett-manus" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/vad-kostar-ett-manus" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: "/vad-kostar-ett-manus", name: TITLE, description: DESCRIPTION })) },
      { type: "application/ld+json", children: JSON.stringify(qaPage({ question: "Vad kostar ett manus till film?", answer: ANSWER, path: "/vad-kostar-ett-manus", authorName: "Fenny" })) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Manus och koncept", path: "/manus-och-koncept" },
            { name: "Vad kostar ett manus", path: "/vad-kostar-ett-manus" },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <LandingPage
        eyebrow="Svar — pris"
        title={<>Vad kostar ett manus till film 2026?</>}
        lede={ANSWER}
        tldr={
          <p>
            Manus till film kostar <strong>4 000–75 000 kr</strong> i Sverige
            2026. Företagsfilm: <strong>8 000–20 000 kr</strong>. Reklamfilm med
            koncept: <strong>25 000–75 000 kr</strong>. Storyboard tillkommer med{" "}
            <strong>6 000–35 000 kr</strong>. Fenny arbetar med fast
            uppdragspris och två revideringar ingår.
          </p>
        }
        ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
        ctaSecondary={{ label: "Manus och koncept", to: "/manus-och-koncept" }}
        trackingId="vad-kostar-ett-manus"
        pricing={{
          heading: "Prisspann per typ av manus",
          intro: "Riktpriser exklusive moms, Sverige 2026.",
          rows: [
            { format: "Kortformat social", scope: "15–60 sek, hook och speakertext", price: "4 000–9 000 kr", lead: "3–5 dagar" },
            { format: "Företagsfilm", scope: "Kärnidé, scenlista, speaker eller intervjuguide", price: "8 000–20 000 kr", lead: "1–2 veckor" },
            { format: "Reklamfilm", scope: "Koncept, idéspår, manus, kanalversioner", price: "25 000–75 000 kr", lead: "2–4 veckor" },
            { format: "Kampanjkoncept", scope: "Plattform och manus för flera filmer", price: "från 60 000 kr", lead: "3–5 veckor" },
            { format: "Storyboard", scope: "12–40 rutor", price: "6 000–35 000 kr", lead: "1–2 veckor" },
            { format: "Manusgranskning", scope: "Skriftlig återkoppling på ert eget manus", price: "4 000–8 000 kr", lead: "3 dagar" },
          ],
          footnote: "Manus ingår utan separat kostnad när vi också producerar filmen inom Pulse eller Prime.",
        }}
        sections={[
          {
            id: "kostnadsdrivare",
            heading: "Fem saker som styr priset",
            body: (
              <>
                <p>
                  <strong>1. Om koncept ingår.</strong> Att skriva ut ett givet
                  koncept kostar hälften mot att hitta idén.{" "}
                  <strong>2. Antal idéspår.</strong> Varje extra spår är ett eget
                  arbete. <strong>3. Antal versioner.</strong> 30/15/6 sekunder
                  plus 9:16 är fyra manus, inte ett.
                </p>
                <p>
                  <strong>4. Godkännandekedjan.</strong> Fem intressenter med
                  synpunkter kostar mer än en beslutsfattare.{" "}
                  <strong>5. Research.</strong> Teknisk eller reglerad bransch
                  kräver faktakontroll som tar tid.
                </p>
              </>
            ),
          },
          {
            id: "exempel",
            heading: "Tre typiska uppdrag",
            body: (
              <>
                <p>
                  <strong>Rekryteringsfilm, 90 sekunder.</strong> Intervjuguide,
                  struktur och speakertext: 12 000 kr, klart på tio dagar.
                </p>
                <p>
                  <strong>Reklamfilm 30 sek + social.</strong> Tre idéspår, valt
                  spår skrivs fram med 15- och 6-sekundersversion samt 9:16:
                  55 000 kr på tre veckor.{" "}
                  <strong>Produktlansering med animation.</strong> Manus plus
                  storyboard som underlag för animatören: 38 000 kr.
                </p>
              </>
            ),
          },
          {
            id: "sa-far-ni-mer",
            heading: "Så får ni mer manus för pengarna",
            body: (
              <p>
                Kom med ett tydligt mål och en utpekad beslutsfattare. Bestäm
                längd och kanaler innan skrivandet börjar. Samla synpunkter i en
                enda omgång i stället för löpande. Och överväg{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/pulse">Pulse-abonnemang</a>{" "}
                om ni behöver flera filmer per år — då ingår manus i månadspriset.
                Jämför också med{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/vad-kostar-en-reklamfilm">vad en reklamfilm kostar</a>{" "}
                totalt.
              </p>
            ),
          },
        ]}
        faq={FAQ}
        caseLimit={4}
        riskReducers
        closing={{
          title: "Vill ni ha en prisuppgift på ert manus?",
          body: "Skicka briefen så återkommer vi med fast pris och tidplan inom ett dygn.",
          primary: { label: "Boka strategimöte", to: "/kontakt" },
        }}
      />
      <RelatedKnowledgeAuto topics={["manus", "pris", "reklamfilm", "process"]} />
    </>
  );
}
